import { cn } from '@/utils/cn';
import {
  LEAVE_STATE_LABELS,
  type LeaveRequest,
  type LeaveState,
} from '../types';

/**
 * États à signaler. Un état normal — « décomptée », c'est-à-dire tout s'est bien
 * passé — n'a pas de pastille : ne signaler que l'exception.
 */
const STATE_TONE: Partial<Record<LeaveState, string>> = {
  submitted: 'bg-warning-50 text-warning-700',
  in_review: 'bg-warning-50 text-warning-700',
  approved: 'bg-success-50 text-success-700',
  rejected: 'bg-error-50 text-error-700',
  cancelled: 'bg-neutral-100 text-neutral-500',
  draft: 'bg-neutral-100 text-neutral-500',
};

function formatRange(startsOn: string, endsOn: string): string {
  const options: Intl.DateTimeFormatOptions = {
    timeZone: 'UTC',
    day: '2-digit',
    month: 'short',
  };
  const start = new Date(`${startsOn}T00:00:00Z`).toLocaleDateString(
    'fr-FR',
    options
  );
  if (startsOn === endsOn) {
    return start;
  }
  const end = new Date(`${endsOn}T00:00:00Z`).toLocaleDateString('fr-FR', {
    ...options,
    year: 'numeric',
  });
  return `${start} → ${end}`;
}

interface LeaveRequestRowProps {
  request: LeaveRequest;
  /** Affiché à la place du libellé du type quand on est côté responsable. */
  personLabel?: string;
  selected?: boolean;
  onSelect: () => void;
  actions?: React.ReactNode;
}

/**
 * Une ligne fine sur grand écran, une carte en deux niveaux sur mobile.
 *
 * L'identité garde toute la largeur disponible sur mobile. Le statut, la durée
 * et les actions passent dessous au lieu d'écraser le nom en une colonne de
 * quelques caractères. Toute la surface reste cliquable.
 */
export function LeaveRequestRow({
  request,
  personLabel,
  selected,
  onSelect,
  actions,
}: LeaveRequestRowProps) {
  const tone = STATE_TONE[request.state];

  return (
    <div
      className={cn(
        // Espace interne (12) ≤ espace externe entre deux lignes (12, donné par
        // `space-y-3` de la liste) : c'est la règle « interne ≤ externe » du
        // standard, celle qui empêche les lignes de se mélanger à l'œil.
        'group relative flex flex-col gap-3 rounded-xl px-3 py-3 transition-colors sm:flex-row sm:items-center sm:gap-4',
        selected ? 'bg-brand-50' : 'hover:bg-neutral-50'
      )}
    >
      <button
        type="button"
        onClick={onSelect}
        className="focus-visible:ring-brand-500 absolute inset-0 rounded-xl focus-visible:ring-2 focus-visible:outline-none"
        aria-label={`Voir le détail de l’absence du ${request.startsOn}`}
      />

      <div className="pointer-events-none w-full min-w-0 sm:flex-1">
        <p className="text-sm font-medium break-words text-neutral-900 sm:truncate">
          {personLabel ?? formatRange(request.startsOn, request.endsOn)}
        </p>
        <p className="text-xs text-neutral-500 sm:truncate">
          {personLabel
            ? formatRange(request.startsOn, request.endsOn)
            : `Politique v${request.policyVersion} · décompte en jours ${request.countingMode}`}
        </p>
      </div>

      <div className="relative flex w-full items-center gap-3 sm:w-auto sm:shrink-0">
        {tone && (
          <span
            className={cn(
              'pointer-events-none inline-flex shrink-0 rounded-full px-2 py-1 text-xs font-medium',
              tone
            )}
          >
            {LEAVE_STATE_LABELS[request.state]}
          </span>
        )}

        <span className="pointer-events-none ml-auto text-right text-sm font-medium text-neutral-900 tabular-nums sm:ml-0 sm:w-16">
          {Number(request.countedDays).toLocaleString('fr-FR')} j
        </span>

        {actions && <div className="flex items-center gap-2">{actions}</div>}
      </div>
    </div>
  );
}
